/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.0.0
 */

import React, { useMemo, useState } from 'react';

import { connect, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { LinkBlock } from '@components/common';
import { IOptionItemProps } from '@components/common/Select/Select';
import { makeSelectorTableExportExtensions } from '@components/Table/selectors/tableSelectors';
import { TableNameType } from '@components/Table/types/tableTypes';
import { TooltipDivStyled } from '@modules/exports/components/styled/ExportButtonStyled';
import { TInstructionLink } from '@modules/internationalization/types/corporate';
import { FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectFeatureEnabled } from '@pages/PageAuth/selectors/authSelectors';
import { IRootReducer } from '@reducers/rootReducer';

import ArrowTooltip from '../../../components/common/Tooltip/Tooltip';
import { makeSelectTranslationCommon } from '../../internationalization/intSelectors';
import { ICommonTranslation } from '../../internationalization/types';
import { modalOpen } from '../../modal/modalActions';
import { IModalOpenPayload } from '../../modal/types/modalActionTypes';
import { IModalRenderProps } from '../../modal/types/modalTypes';
import { setExportFormat, startExport } from '../exportActions';
import { ISetExportFormatPayload } from '../exportTypes';
import ExportButtonSelect from './ExportButtonSelect';
import ExportMessage from './ExportMessage';

export interface IExportButtonProps {
    disabled?: boolean;
    disableMessage?: string;
    exportExtensionsPreset?: string[];
    src: string;
    tableName: TableNameType;
    instructionLink?: TInstructionLink;
}

type TExportButton = IExportButtonProps & IConnectedProps & IDispatchProps;

export const ExportButton = ({
                                 disabled,
                                 disableMessage,
                                 setExportFormat,
                                 openModal,
                                 tableName,
                                 translation,
                                 startExport,
                                 exportExtensions,
                                 exportExtensionsPreset,
                                 instructionLink,
                             }: TExportButton) => {

    const isInstructionsLinksEnabled = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.INSTRUCTIONS_LINKS));

    const isInstructionTooltipShown =
        isInstructionsLinksEnabled &&
        !!instructionLink &&
        !!instructionLink.instructionLink;

    const extensions = useMemo(() => exportExtensions.length ? exportExtensions : exportExtensionsPreset,
        [exportExtensions, exportExtensionsPreset]);

    const tooltipMessage = useMemo(() => {
        if (disabled) {
            return disableMessage
        } else if (isInstructionTooltipShown) {
            return <TooltipDivStyled>
                {instructionLink.tooltipText}
                <LinkBlock
                    isExternalUrl
                    target={'_blank'}
                    url={instructionLink.instructionLink}
                >
                    {instructionLink?.linkText}
                </LinkBlock>
            </TooltipDivStyled>
        }
    },[
        disabled,
        isInstructionTooltipShown,
        disableMessage,
        instructionLink?.tooltipText,
        instructionLink?.instructionLink,
        instructionLink?.linkText,
    ]);
    const [tooltipToShow, setTooltipToShow] = useState(tooltipMessage);

    const onSelectHandler = (item: IOptionItemProps) => {
        setExportFormat({
            fileFormat: item.value.toString(),
            tableName,
        });
        openModal({
            render: (p: IModalRenderProps) => <ExportMessage
                handleClose={p.onCloseModal}
                handleSubmit={() => handleSubmit(p.onCloseModal)}
                translation={translation}
            />,
        });
    };

    const handleSubmit = (onCloseModal: () => void) => {
        startExport();
        onCloseModal();
    };

    if (!extensions?.length) {
        return null;
    }

    return (
        <ArrowTooltip
            message={tooltipToShow}
            placement={'top'}
        >
            <TooltipDivStyled
                onClick={() => setTooltipToShow('')}
                onMouseLeave={() => setTooltipToShow(tooltipMessage)}
            >
                <ExportButtonSelect
                    disabled={disabled}
                    exportExtensions={extensions}
                    translation={translation}
                    onSelectHandler={onSelectHandler}
                />
            </TooltipDivStyled>
        </ArrowTooltip>
    );
};

export interface IConnectedProps {
    exportExtensions: string[];
    translation: ICommonTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IExportButtonProps, IConnectedProps>({
    exportExtensions: makeSelectorTableExportExtensions(),
    translation: makeSelectTranslationCommon(),
});

export interface IDispatchProps {
    openModal: (payload: IModalOpenPayload) => void;
    setExportFormat: (v: ISetExportFormatPayload) => void;
    startExport: () => void;
}

const mapDispatchToProps = (dispatch: Dispatch, props: TExportButton): IDispatchProps => ({
    openModal: (payload: IModalOpenPayload) => dispatch(modalOpen(payload)),
    setExportFormat: (payload: ISetExportFormatPayload) => dispatch(setExportFormat(payload)),
    startExport: () => dispatch(startExport(props.src)),
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default connect(mapStateToProps, mapDispatchToProps)(ExportButton) as unknown as React.FC<any>;
